<script setup lang="ts">
import {DeliveryMethod} from "@/shared/graphql/types";
import {RadioCard, RadioCardGroup} from "@/shared/components/ui/radio-group";
import {faBuilding, faTruck} from "@fortawesome/pro-solid-svg-icons";
import {computed, watch} from "vue";

interface Props {
  deliveryMethods: DeliveryMethod[];
  disabled?: boolean;
}

// No more "selected" prop in the public API—v-model now binds a full DeliveryMethod.
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

// Define our internal model as a DeliveryMethod.
const modelValue = defineModel<DeliveryMethod>();

const selectedId = computed<string>({
  get: () => {
    // If modelValue is not set, return an empty string.
    return modelValue?.value?.id ?? "";
  },
  set: (newId: string) => {
    // Find the corresponding DeliveryMethod based on the selected id.
    const selectedMethod = props.deliveryMethods.find(
        (deliveryMethod) => deliveryMethod.id === newId
    );
    if (selectedMethod) {
      modelValue.value = selectedMethod;
    }
  },
});

const emit = defineEmits<{ (e: 'deliveryMethod:selected', deliveryMethod: DeliveryMethod): void }>();
watch(selectedId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    emit('deliveryMethod:selected', modelValue.value);
  }
});

const deliveryMethodIcon = (deliveryMethod: DeliveryMethod) => {
  return deliveryMethod.kind === 'PICKUP' ? faBuilding : faTruck;
};

const deliveryMethodLabel = (deliveryMethod: DeliveryMethod) => {
  //@ts-ignore
  if (deliveryMethod['cost']) return deliveryMethod.cost.formatted;
  return deliveryMethod.description;
};
</script>

<template>
  <RadioCardGroup
      v-model="selectedId"
      :disabled="props.disabled"
      :col-span="2"
      :cols="2"
  >
    <RadioCard
        v-for="deliveryMethod in props.deliveryMethods"
        :key="deliveryMethod.id"
        :value="deliveryMethod.id"
        :label="deliveryMethod.title"
        :disabled="props.disabled"
        :icon="deliveryMethodIcon(deliveryMethod)"
        :description="deliveryMethodLabel(deliveryMethod)"
    />
  </RadioCardGroup>
</template>

<style scoped>
/* Your component styles here */
</style>